import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Search, 
  MapPin, 
  Calendar as CalendarIcon, 
  Filter,
  Loader2,
  Trophy,
  Users,
  GraduationCap,
  Heart
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, [stateFilter, typeFilter, startDate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.append('state', stateFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      params.append('limit', '50');

      const response = await axios.get(`${API_URL}/events?${params}`);
      setEvents(response.data.events);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'tournament':
        return Trophy;
      case 'league_night':
        return Users;
      case 'clinic':
        return GraduationCap;
      case 'charity':
        return Heart;
      default:
        return CalendarIcon;
    }
  };

  const eventTypes = [
    { value: '', label: 'All Types' },
    { value: 'tournament', label: 'Tournament' },
    { value: 'league_night', label: 'League Night' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'charity', label: 'Charity' },
    { value: 'meetup', label: 'Meetup' }
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      {/* Header */}
      <div className="bg-deep-green py-12 md:py-16">
        <div className="container-padding">
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
            Events Calendar
          </h1>
          <p className="text-cream/80 font-body text-lg max-w-2xl">
            Discover tournaments, clinics, charity events, and networking opportunities 
            in the minority golf community.
          </p>
        </div>
      </div>

      <div className="container-padding py-8">
        {/* Search & Filters */}
        <div className="bg-white rounded-sm shadow-card p-4 md:p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="events-search-input"
              />
            </div>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="h-10 px-4 border border-input rounded-sm bg-background font-body text-sm"
              data-testid="events-state-filter"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-10 px-4 border border-input rounded-sm bg-background font-body text-sm"
              data-testid="events-type-filter"
            >
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className={cn("justify-start text-left font-normal", !startDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'MMM d, yyyy') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button type="submit" className="btn-gold" data-testid="events-search-btn">
              <Filter className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <CalendarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-xl text-deep-green mb-2">No Events Found</h3>
            <p className="text-muted-foreground font-body">
              Try adjusting your search filters or check back later.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const EventIcon = getEventIcon(event.type);
              const eventDate = new Date(event.start_at);
              
              return (
                <Link key={event.event_id} to={`/events/${event.event_id}`}>
                  <Card className="card-premium hover-lift h-full" data-testid={`event-${event.event_id}`}>
                    {event.flyer_image_url && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img 
                          src={event.flyer_image_url} 
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-5">
                      {/* Date Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-fairway text-white px-3 py-1.5 rounded-sm">
                          <p className="text-xs font-semibold uppercase">
                            {format(eventDate, 'MMM')}
                          </p>
                          <p className="text-xl font-display font-bold">
                            {format(eventDate, 'd')}
                          </p>
                        </div>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <EventIcon className="w-3 h-3" />
                          {event.type?.replace('_', ' ')}
                        </Badge>
                      </div>

                      <h3 className="font-display font-semibold text-lg text-deep-green mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                        <MapPin className="w-4 h-4 shrink-0" />
                        {event.city}, {event.state}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-gold font-semibold">
                          {event.entry_fee === 0 || event.entry_fee === null 
                            ? 'Free' 
                            : `$${event.entry_fee}`
                          }
                        </span>
                        {event.format && (
                          <span className="text-muted-foreground text-sm">
                            {event.format}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
